/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {LookupService} from '../../common/service/lookup.service';
import {MapToolsService} from './map-tools.service';
import {GraphicSelectionService} from './graphic-selection.service';
import {SelectionUtilityService} from './selection-utility.service';
import {SpatialUtilService} from './spatial-util.service';
import {LayerUtilService} from '../layers/layer-util.service';
import {NgEventBus} from 'ng-event-bus';

@Injectable({
    providedIn: 'root'
})
export class MapEventsSelectionService {
    private lastClickedAnnotationId: any;
    graphicToModify: any;

    constructor(
        private lookupService: LookupService,
        private mapToolsService: MapToolsService,
        private graphicSelectionService: GraphicSelectionService,
        private selectionUtilityService: SelectionUtilityService,
        private spatialUtilService: SpatialUtilService,
        private layerUtilService: LayerUtilService,
        private eventBus: NgEventBus
    ) {}

    title = 'MapEventsSelectionService';
    lastFeatureLayer = undefined;
    lastFeature = undefined;
    enableInterestAnnotationClick = false;

    pointLayerIds = ['angleFeatureLayer', 'annotationFeatureLayer', 'pointFeatureLayer', 'constructPointFeatureLayer', 'controlPointFeatureLayer'];

    pendingHighlightsMap = {};

    private setPendingHighlights(featureLayer, features) {
        const featureClass = this.spatialUtilService.getFeatureClass(featureLayer);
        let featureIds = features ? features.map(f => f.attributes.id) : [];
        if (
            this.lastFeature !== undefined &&
            (featureClass === 'annotation' || featureClass === 'angle') &&
            featureIds.length === 1 &&
            this.lastFeature.length === 1 &&
            featureIds[0] === this.lastFeature[0].attributes.id &&
            this.lastClickedAnnotationId !== undefined
        ) {
            this.lastClickedAnnotationId = undefined;
            featureIds = [];
        }

        if (!this.pendingHighlightsMap[featureClass]) {
            this.pendingHighlightsMap[featureClass] = [];
        }
        const existingFeatureIds = this.pendingHighlightsMap[featureClass];
        existingFeatureIds.push(...featureIds.filter(featureId => !existingFeatureIds.includes(featureId)));
    }

    private applyPendingHighlights() {
        Object.keys(this.pendingHighlightsMap).forEach(k => {
            this.spatialUtilService.highlightFeatures(k, this.pendingHighlightsMap[k]);
            delete this.pendingHighlightsMap[k];
        });
    }

    selectOnClick(evt, map, interestSelection, spatialReference, scale) {
        console.debug(this.title + ' : selectOnClick - user clicked on or near');
        if (
            (evt.composedPath()[1].id === 'annotationFeatureLayer_layer' || evt.composedPath()[1].id === 'angleFeatureLayer_layer') &&
            evt.composedPath()[0].nextSibling.attributes[4] !== undefined
        ) {
            this.lastClickedAnnotationId = evt.composedPath()[0].e_graphic.attributes.id;
        }

        const defaultDistanceTolerance = (scale || 1) / 1000;

        const featuresSelectable = {};
        const featuresLayer = {};

        const pixelScaleFactor = this.layerUtilService.getPixelScaleFactor();

        const layerFeaturesList = this.graphicSelectionService.featureLayers
            .map(featureLayer => this.spatialUtilService.getGraphics(featureLayer))
            .map((v, i) => {
                const featureLayer = this.graphicSelectionService.featureLayers[i];
                const featureClass = this.spatialUtilService.getFeatureClass(featureLayer);
                return {
                    layer: featureLayer,
                    features: v.filter(feature => {
                        const featureGeometry = this.layerUtilService.getSpatialQueryFeatureGeometry(featureLayer, feature);
                        if (['angle', 'annotation'].includes(featureClass) && featureGeometry.type === 'polygon') {
                            return featureGeometry.contains(evt.mapPoint);
                        } else if (['constructpoint', 'controlpoint', 'point'].includes(featureClass) && featureGeometry.type === 'polygon') {
                            const distanceTolerance = Math.max(featureGeometry.getExtent().getWidth() / 2, defaultDistanceTolerance);
                            return this.spatialUtilService.distance(evt.mapPoint, feature.geometry) < distanceTolerance;
                        } else if (['constructline', 'line'].includes(featureClass)) {
                            const distanceTolerance = Math.max(
                                defaultDistanceTolerance,
                                (0.5 * this.layerUtilService.getPointerEventsLineWidthPixelScaleFactor(pixelScaleFactor) * map.extent.getWidth()) /
                                    map.width
                            );
                            return this.spatialUtilService.distance(evt.mapPoint, feature.geometry) < distanceTolerance;
                        } else if (featureClass === 'polyline') {
                            const distanceTolerance = Math.max(
                                defaultDistanceTolerance,
                                (0.5 * this.layerUtilService.getPointerEventsLineWidthPixelScaleFactor(pixelScaleFactor) * map.extent.getWidth()) /
                                    map.width
                            );
                            return this.spatialUtilService.distance(evt.mapPoint, featureGeometry) < distanceTolerance;
                        } else {
                            return this.spatialUtilService.distance(evt.mapPoint, featureGeometry) < defaultDistanceTolerance;
                        }
                    })
                };
            })
            .filter(v => v.features.length);

        if (layerFeaturesList.length === 0 && !this.selectionUtilityService.multiSelectEnabled()) {
            this.spatialUtilService.unhighlightAllFeatures();

            if (this.mapToolsService.isActive('CONSTRUCTION_TOOLS_WINDOW')) {
                this.eventBus.cast('deselectpolygonFeatures');
            }

            if (this.mapToolsService.isActive('IDENTIFY_FEATURES_WINDOW')) {
                this.eventBus.cast('clearIdentifyFeatures');
            }

            return;
        }

        const filteredLayerFeaturesList = [];

        if (layerFeaturesList.length) {
            if (this.mapToolsService.isActive('IDENTIFY_FEATURES')) {
                filteredLayerFeaturesList.push(...layerFeaturesList);
            } else {
                const orderedLayerIds = [
                    'pointFeatureLayer',
                    'controlPointFeatureLayer',
                    'constructPointFeatureLayer',
                    'lineFeatureLayer',
                    'constructLineFeatureLayer',
                    'polylineFeatureLayer',
                    'angleFeatureLayer',
                    'annotationFeatureLayer',
                    'polygonFeatureLayer'
                ];

                filteredLayerFeaturesList.push(
                    layerFeaturesList.slice().sort((a, b) => {
                        const aIdx = orderedLayerIds.indexOf(a.layer.id);
                        const bIdx = orderedLayerIds.indexOf(b.layer.id);

                        if (aIdx === -1 && bIdx === -1) {
                            return 0;
                        }

                        if (aIdx === -1) {
                            return 1;
                        }

                        if (bIdx === -1) {
                            return -1;
                        }

                        return aIdx - bIdx;
                    })[0]
                );
            }
        }

        filteredLayerFeaturesList.forEach(v => {
            const featureLayer = v.layer;
            featuresLayer[featureLayer.id] = featureLayer;
            featuresSelectable[featureLayer.id] = v.features;

            if (interestSelection && featureLayer.id === 'polygonFeatureLayer') {
                this.selectInterestPolygonHierarchy(map, featuresSelectable['polygonFeatureLayer'], featureLayer);
            } else if (interestSelection && featureLayer.id === 'lineFeatureLayer') {
                this.selectInterestPolylineHierarchy(map, featuresSelectable['lineFeatureLayer'], featureLayer);
            } else if (this.pointLayerIds.includes(featureLayer.id)) {
                const selectionCount = this.pointLayerIds
                    .filter(id => featuresSelectable[id])
                    .reduce((acc, id) => acc + featuresSelectable[id].length, 0);

                if (
                    selectionCount === 1 &&
                    (this.spatialUtilService.getFeatureHighlightsCount() === 0 || !this.selectionUtilityService.multiSelectEnabled())
                ) {
                    this.selectPointGraphic(map, featuresLayer, featuresSelectable);
                    this.graphicToModify = undefined;
                } else {
                    this.selectAllPointGraphics(map, featuresLayer, featuresSelectable);
                }
            } else if (featureLayer.id === 'lineFeatureLayer') {
                const filteredFeatures = this.graphicSelectionService.applyFeaturesFilter('line', featuresSelectable['lineFeatureLayer']);
                this.selectGraphic(map, featuresLayer['lineFeatureLayer'], filteredFeatures);
            } else if (featureLayer.id === 'constructLineFeatureLayer') {
                this.selectGraphic(map, featuresLayer['constructLineFeatureLayer'], featuresSelectable['constructLineFeatureLayer']);
            } else if (featureLayer.id === 'polylineFeatureLayer') {
                const lines = featuresSelectable['lineFeatureLayer'] || [];
                const lineIds = lines.map(p => p.attributes.id);
                const polylineIds = featuresSelectable['polylineFeatureLayer'].map(p => p.attributes.id).filter(id => !lineIds.includes(id));
                const linesToSelect = [...lines, ...this.spatialUtilService.getLineGraphics().filter(l => polylineIds.includes(l.attributes.id))];
                this.selectGraphic(map, this.spatialUtilService.getMapFeatureLayer('line'), linesToSelect);
            } else if (featureLayer.id === 'polygonFeatureLayer') {
                const filteredFeatures = this.graphicSelectionService.applyFeaturesFilter('polygon', featuresSelectable['polygonFeatureLayer']);
                if (filteredFeatures.length) {
                    this.selectInterestPolygonHierarchy(map, filteredFeatures, featureLayer);
                } else {
                    this.graphicToModify = undefined;
                }
            } else if (featureLayer.id === 'enlargementFeatureLayer') {
                this.selectGraphic(map, featuresLayer['enlargementFeatureLayer'], featuresSelectable['enlargementFeatureLayer']);
            } else if (featureLayer.id === 'clippingFeatureLayer') {
                this.selectGraphic(map, featuresLayer['clippingFeatureLayer'], featuresSelectable['clippingFeatureLayer']);
            }
        });

        this.applyPendingHighlights();
    }

    checkAndUnhighlight(map, features, featureLayer) {
        if (this.selectionUtilityService.enlargementGraphic(map, false)) {
            const enlargementGraphic = this.selectionUtilityService.enlargementGraphic(map, true);
            this.eventBus.cast('selectFeaturesForEnlargement', {
                enlargementGraphic,
                function(annotations, angles) {
                    let dataAvailable = false;
                    const unhighlightFeatures = [];

                    features.forEach(feature => {
                        let found = false;

                        annotations.forEach(item => {
                            if (feature.attributes.attribute1 && feature.attributes.id === item.attributes.id) {
                                found = true;
                            }
                        });

                        angles.forEach(item => {
                            if (
                                feature.attributes.anglefromlineid &&
                                feature.attributes.angletolineid &&
                                feature.attributes.id === item.attributes.id
                            ) {
                                found = true;
                            }
                        });

                        if (found === false) {
                            dataAvailable = true;
                            unhighlightFeatures.push(feature);
                        }
                    });

                    if (dataAvailable) {
                        this.spatialUtilService.unhighlightFeatures(featureLayer, unhighlightFeatures);
                    }
                }
            });
        } else {
            this.spatialUtilService.unhighlightFeatures(featureLayer, features);
        }
    }

    private selectGraphic(map, featureLayer, features) {
        console.debug(this.title + ' : selectGraphic - toggle select feature on/off');

        this.enableInterestAnnotationClick = !this.enableInterestAnnotationClick && this.mapToolsService.isActive('ATTRIBUTE_VIEWER_WINDOW');

        if (features) {
            if (this.selectionUtilityService.multiSelectEnabled()) {
                if (this.spatialUtilService.someFeaturesHighlighted(featureLayer, features)) {
                    this.checkAndUnhighlight(map, features, featureLayer);
                    return;
                }
            } else {
                this.spatialUtilService.unhighlightAllFeatures();
            }

            this.setPendingHighlights(featureLayer, features);
            this.lastFeatureLayer = featureLayer;
            this.lastFeature = features;
        }
    }

    private selectInterestPolygonHierarchy(map, features, layer) {
        if (features.length) {
            const legalParcelTypeLookups = this.lookupService.getLookups('legalParcelType');

            this.selectGraphic(
                map,
                layer,
                features
                    .filter(f => f.attributes.type !== 97)
                    .slice()
                    .reverse()
                    .sort((p1, p2) => {
                        const lpt1 = legalParcelTypeLookups.find(l => l.id === p1.attributes.type);
                        const lpt2 = legalParcelTypeLookups.find(l => l.id === p2.attributes.type);
                        if (lpt1 && lpt2) {
                            if (lpt1.businessClass === 'Interest' && lpt2.businessClass === 'Interest') {
                                if (lpt1.permitInterestLinking === lpt2.permitInterestLinking) {
                                    return 0;
                                }
                                if (lpt1.permitInterestLinking) {
                                    return -1;
                                }
                                if (lpt2.permitInterestLinking) {
                                    return 1;
                                }
                            }
                            if (lpt1.businessClass === 'Interest') {
                                return -1;
                            }
                            if (lpt2.businessClass === 'Interest') {
                                return 1;
                            }
                            return 0;
                        }
                        if (lpt1) {
                            return -1;
                        }
                        if (lpt2) {
                            return 1;
                        }
                        return 0;
                    })
                    .slice(0, 1)
            );
        }
    }

    private selectInterestPolylineHierarchy(map, features, layer) {
        const validPolylines = [];

        const allowedTypes = this.lookupService.getLookups('polylineType');
        features.forEach(feature => {
            allowedTypes.forEach(allowedType => {
                if (feature.attributes.type === allowedType.id) {
                    validPolylines.push(feature);
                }
            });
        });

        if (validPolylines.length > 0) {
            this.selectGraphic(map, layer, validPolylines);
        }
    }

    getPolygonTypesLookup() {
        console.debug(this.title + ' : getInterestTypesLookup');
        return this.lookupService.getLookups('legalParcelType').filter(item => item.permitInterestLinking === true);
    }

    getPointTypeLookups() {
        return this.lookupService.getLookups('pointType');
    }

    private selectPointGraphic(map: any, featuresLayer: any, featureSets: any) {
        console.debug(`${this.title} - selectPointGraphic`);

        let foundFeatureLayer: any;
        const featuresArray = [];

        if (!this.selectionUtilityService.multiSelectEnabled()) {
            this.spatialUtilService.unhighlightAllFeatures();
        }

        for (const [layerId, features] of Object.entries(featureSets) as any[]) {
            if (this.pointLayerIds.includes(layerId)) {
                this.graphicSelectionService.applyFeaturesFilter(layerId.replace('FeatureLayer', '').toLowerCase(), features).forEach(feature => {
                    featuresArray.push(feature);
                    foundFeatureLayer = featuresLayer[layerId];
                });
            }
        }

        if (foundFeatureLayer) {
            this.selectGraphic(map, foundFeatureLayer, featuresArray);
        }
    }

    private selectAllPointGraphics(map: any, featuresLayer: any, featureSets: any) {
        console.debug(`${this.title} - selectAllPointGraphics`);

        if (!this.selectionUtilityService.multiSelectEnabled()) {
            this.spatialUtilService.unhighlightAllFeatures();
        }

        for (const [layerId, features] of Object.entries(featureSets) as any[]) {
            if (this.pointLayerIds.includes(layerId)) {
                const filteredFeatures = this.graphicSelectionService.applyFeaturesFilter(
                    layerId.replace('FeatureLayer', '').toLowerCase(),
                    features
                );
                if (!this.selectionUtilityService.multiSelectEnabled()) {
                    this.spatialUtilService.highlightFeatures(layerId, filteredFeatures);
                    this.lastFeatureLayer = featuresLayer[layerId];
                    this.lastFeature = filteredFeatures;
                } else {
                    if (this.spatialUtilService.someFeaturesHighlighted(layerId, filteredFeatures)) {
                        this.checkAndUnhighlight(map, filteredFeatures, featuresLayer[layerId]);
                    } else {
                        this.spatialUtilService.highlightFeatures(layerId, filteredFeatures);
                        this.lastFeatureLayer = featuresLayer[layerId];
                        this.lastFeature = filteredFeatures;
                    }
                }
            }
        }
    }
}
